import React, { useState, useRef, useEffect } from 'react';
import './GameScreen.scss';
import { ITEMS_TO_FIND, GAME_TIME } from '../../constants';
import Timer from '../Timer/Timer';
import ItemsList from '../ItemsList/ItemsList';

interface GameScreenProps {
  onWin: (foundItems: string[]) => void;
  onTryAgain: () => void;
  isWon: boolean;
}

// Original image dimensions
const IMAGE_WIDTH = 1920;
const IMAGE_HEIGHT = 1080;

const GameScreen: React.FC<GameScreenProps> = ({ onWin, onTryAgain, isWon }) => {
  const [foundItems, setFoundItems] = useState<string[]>([]);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isTimeUp, setIsTimeUp] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Initialize and play background music
    audioRef.current = new Audio(`${process.env.PUBLIC_URL}/holidaymusic.mp3`);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.5;

    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.log('Audio autoplay prevented:', error);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isWon && audioRef.current) {
      audioRef.current.pause();
    }
  }, [isWon])

  useEffect(() => {
    const updateScale = () => {
      if (imgRef.current && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();

        // Calculate scale based on object-fit: contain
        const scaleX = containerRect.width / IMAGE_WIDTH;
        const scaleY = containerRect.height / IMAGE_HEIGHT;
        const newScale = Math.min(scaleX, scaleY);

        // Calculate offset (centered image)
        const renderedWidth = IMAGE_WIDTH * newScale;
        const renderedHeight = IMAGE_HEIGHT * newScale;
        const offsetX = (containerRect.width - renderedWidth) / 2;
        const offsetY = (containerRect.height - renderedHeight) / 2;

        setScale(newScale);
        setOffset({ x: offsetX, y: offsetY });
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  const handleTimeUp = () => {
    if (foundItems.length === ITEMS_TO_FIND.length) {
      onWin(foundItems);
    } else {
      setIsTimeUp(true);
    }
  };

  const playSuccessSound = () => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  const handleItemClick = (item: string) => {
    if (isTimeUp) return; // Disable clicks after time is up

    if (!foundItems.includes(item)) {
      playSuccessSound();
      const newFoundItems = [...foundItems, item];
      setFoundItems(newFoundItems);

      if (newFoundItems.length === ITEMS_TO_FIND.length) {
        setTimeout(() => onWin(newFoundItems), 500);
      }
    }
  };

  // Convert position from original 1920x1080 coordinates to screen position
  const getScreenPosition = (x: number, y: number) => {
    return {
      left: offset.x + x * scale,
      top: offset.y + y * scale,
    };
  };

  // Item positions in original 1920x1080 coordinates
  const itemPositions: Record<string, { x: number; y: number }> = {
    'DEER': { x: 1234, y: 422 },
    'HOLIDAY CARD': { x: 200, y: 126 },
    'HUSKEY DOG': { x: 1558, y: 909 },
    'WREATH': { x: 1484, y: 735 },
    'GREEN ORNAMENT': { x: 417, y: 987 },
    'ANGEL': { x: 407, y: 783 },
    'ICE SKATES': { x: 1010, y: 962 },
    'SANTA HAT': { x: 954, y: 290 },
    'STOCKING': { x: 71, y: 400 },
    'HOT CHOCOLATE': { x: 1400, y: 904 },
    'NUTCRACKER': { x: 96, y: 890 },
    'WHITE BIRD': { x: 1750, y: 331 },
    'CANDY CANE': { x: 1807, y: 937 },
    'GREEN BOW': { x: 221, y: 585 },
    'POLAR BEAR': { x: 714, y: 797 },
    'MITTENS': { x: 1456, y: 228 },
    'RUBBER DUCK': { x: 304, y: 501 },
    'UGLY SWEATER': { x: 1817, y: 811 },
    'RACOON': { x: 497, y: 595 },
  };

  // UI positions in original coordinates
  const listPosition = getScreenPosition(270, 30);
  const timerPosition = getScreenPosition(810, 50);

  return (
    <div className="game-screen" ref={containerRef}>
      <img
        ref={imgRef}
        src={`${process.env.PUBLIC_URL}/images/start/base_background.png`}
        alt="Game background"
        className="game-screen__bg"
        onLoad={() => {
          if (imgRef.current && containerRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const scaleX = containerRect.width / IMAGE_WIDTH;
            const scaleY = containerRect.height / IMAGE_HEIGHT;
            const newScale = Math.min(scaleX, scaleY);
            const renderedWidth = IMAGE_WIDTH * newScale;
            const renderedHeight = IMAGE_HEIGHT * newScale;
            setScale(newScale);
            setOffset({
              x: (containerRect.width - renderedWidth) / 2,
              y: (containerRect.height - renderedHeight) / 2,
            });
          }
        }}
      />
      <div className="game-screen__content">
        <div
          className="game-screen__list"
          style={{ left: listPosition.left, top: listPosition.top, transform: `scale(${scale})` }}
        >
          <ItemsList items={ITEMS_TO_FIND} foundItems={foundItems} />
        </div>

        <div
          className="game-screen__timer"
          style={{ left: timerPosition.left, top: timerPosition.top, transform: `scale(${scale})` }}
        >
          <Timer initialTime={GAME_TIME} onTimeUp={handleTimeUp} />
          {isTimeUp && (
            <button className="game-screen__try-again" onClick={onTryAgain}>
              TRY AGAIN
            </button>
          )}
        </div>

        <div className="game-screen__clickable-areas">
          {ITEMS_TO_FIND.map((item) => {
            const pos = itemPositions[item];
            if (!pos) return null;
            const { left, top } = getScreenPosition(pos.x, pos.y);

            return (
              <div
                key={item}
                className={`game-screen__clickable-area ${
                  foundItems.includes(item) ? 'game-screen__clickable-area--found' : ''
                }`}
                style={{
                  left,
                  top,
                  width: 60 * scale,
                  height: 60 * scale,
                }}
                onClick={() => handleItemClick(item)}
                title={item}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GameScreen;
